import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiInfo, FiDollarSign, FiPercent, FiHash, FiPlusCircle } = FiIcons;

const InfoPopup = ({ isOpen, onClose, type, darkMode }) => {
  if (!isOpen) return null;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const popupVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { opacity: 1, scale: 1, y: 0 },
  };

  const renderContent = () => {
    switch (type) {
      case 'disclaimer':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded">
                <SafeIcon icon={FiInfo} className="text-lg text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Margin Disclaimer</h3>
            </div>
            <div className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <p>This margin calculator is provided for informational purposes only. Calculations should not be considered financial advice.</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Verify results with your accounting system</li>
                <li>Consider overheads and taxes</li>
                <li>Consult professionals for business decisions</li>
              </ul>
            </div>
          </div>
        );

      case 'calculation':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded">
                <SafeIcon icon={FiPercent} className="text-lg text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Margin Formulas</h3>
            </div>
            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-3">
                <h4 className="font-semibold text-gray-800 dark:text-white text-sm">Profit</h4>
                <p className="text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded text-sm">Revenue - Cost Price</p>
              </div>
              <div className="border-l-4 border-blue-500 pl-3">
                <h4 className="font-semibold text-gray-800 dark:text-white text-sm">Margin %</h4>
                <p className="text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded text-sm">(Profit ÷ Revenue) × 100</p>
              </div>
            </div>
          </div>
        );

      case 'gst-calculation':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded">
                <SafeIcon icon={FiPlusCircle} className="text-lg text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">GST Calculation (10%)</h3>
            </div>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-3">
                <h4 className="font-semibold text-gray-800 dark:text-white text-sm">Adding GST</h4>
                <p className="text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded text-sm">Base × 0.10 = GST Amount</p>
                <p className="text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded text-sm mt-1">Base + GST = Total</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-3">
                <h4 className="font-semibold text-gray-800 dark:text-white text-sm">Removing GST</h4>
                <p className="text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded text-sm">Total ÷ 1.10 = Base Price</p>
                <p className="text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded text-sm mt-1">Total - Base = GST Amount</p>
              </div>
            </div>
          </div>
        );

      case 'gst-disclaimer':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded">
                <SafeIcon icon={FiInfo} className="text-lg text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">GST Disclaimer</h3>
            </div>
            <div className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <p>This tool calculates Goods and Services Tax at a fixed rate of 10% (standard Australian rate).</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Specific items may be GST-free or have different rates</li>
                <li>Ensure you are registered for GST before charging it</li>
                <li>Keep accurate records for BAS/Tax reporting</li>
              </ul>
            </div>
          </div>
        );

      case 'calc-disclaimer':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded">
                <SafeIcon icon={FiHash} className="text-lg text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Calculator Disclaimer</h3>
            </div>
            <div className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
              <p>Standard mathematical order of operations (BODMAS/PEMDAS) is applied.</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Floating point math may result in minor rounding variances</li>
                <li>Verify critical calculations manually</li>
                <li>intermediate results are displayed in real-time</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        variants={popupVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-sm w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
          {renderContent()}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200"
            >
              Got it
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InfoPopup;